import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import CompanyCard from '../components/CompanyCard';
import { toast } from 'react-toastify';
import useAxios from '../api/axios';
import { useSpotContext } from '../context/SpotProvider';
import { useAuth } from '../context/AuthProvider';

function MyProfile({ selectView = false }) {
  const navigate = useNavigate();
  const { selectedCompanyID } = useSpotContext();
  const { auth, setAuth } = useAuth();
  const api = useAxios();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });

  const profileName = auth?.user?.name ?? auth?.user?.fullName ?? '';
  const profilePhone = auth?.user?.phone ?? auth?.user?.phoneNumber ?? '';
  const profileEmail = auth?.user?.email ?? '';

  useEffect(() => {
    setProfileForm({
      name: profileName,
      phone: profilePhone,
      email: profileEmail,
    });
  }, [profileName, profilePhone, profileEmail]);

  const handleProfileChange = (field) => (event) => {
    setProfileForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const resetProfileForm = () => {
    setProfileForm({
      name: profileName,
      phone: profilePhone,
      email: profileEmail,
    });
    setIsEditingProfile(false);
  };

  const closePasswordModal = () => {
    setPasswordForm({
      password: '',
      confirmPassword: '',
    });
    setIsPasswordModalOpen(false);
  };

  const saveProfile = async () => {
    const trimmedName = profileForm.name.trim();
    const trimmedPhone = profileForm.phone.trim();
    const trimmedEmail = profileForm.email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!trimmedName) {
      toast.warn('Syötä nimi');
      return;
    }

    if (!trimmedPhone) {
      toast.warn('Syötä puhelinnumero');
      return;
    }

    if (!emailRegex.test(trimmedEmail)) {
      toast.warn('Tarkista sähköpostiosoite');
      return;
    }

    const payload = {
      name: trimmedName,
      phone: trimmedPhone,
      email: trimmedEmail,
    };

    try {
      await api.post('/editUser', JSON.stringify(payload), {
        headers: { 'Content-Type': 'application/json' },
      });

      setAuth({
        ...auth,
        user: {
          ...auth?.user,
          name: trimmedName,
          fullName: trimmedName,
          phone: trimmedPhone,
          phoneNumber: trimmedPhone,
          email: trimmedEmail,
        },
      });

      setIsEditingProfile(false);
      toast.success('Käyttäjätiedot päivitetty');
    } catch (err) {
      if (!err?.response) {
        toast.error('Ei vastausta palvelimelta');
      } else if (err.response?.status === 400) {
        toast.error('Virheellinen käyttäjädata');
      } else if (err.response?.status === 401) {
        toast.error('Kirjautuminen on vanhentunut');
      } else {
        toast.error('Käyttäjätietojen päivitys epäonnistui');
      }
    }
  };

  const savePassword = async () => {
    const { password, confirmPassword } = passwordForm;

    if (password.length < 8) {
      toast.warn('Salasanan minimipituus on 8 merkkiä');
      return;
    }

    if (password !== confirmPassword) {
      toast.warn('Salasanat eivät täsmää');
      return;
    }

    try {
      await api.post('/login.php?action=changePwd', JSON.stringify({ password }), {
        headers: { 'Content-Type': 'application/json' },
      });

      closePasswordModal();
      toast.success('Salasana vaihdettu');
    } catch (err) {
      if (!err?.response) {
        toast.error('Ei vastausta palvelimelta');
      } else if (err.response?.status === 400) {
        toast.error('Salasanan vaihto epäonnistui');
      } else if (err.response?.status === 401) {
        toast.error('Kirjautuminen on vanhentunut');
      } else {
        toast.error('Salasanan tallennus epäonnistui');
      }
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 pb-8">
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-2xl font-bold text-slate-900">Käyttäjätiedot</span>

          <div className="flex flex-wrap gap-2">
            <Button name="Vaihda salasana" type="PRIMARY" action={() => setIsPasswordModalOpen(true)} className="mb-0 rounded-xl px-4 py-2 text-sm font-bold" />

            {!isEditingProfile ? (
              <Button name="Muokkaa" type="EDIT" action={() => setIsEditingProfile(true)} className="mb-0 rounded-xl px-4 py-2 text-sm font-bold" />
            ) : (
              <>
                <Button name="Tallenna" type="PRIMARY" action={saveProfile} className="mb-0 rounded-xl px-4 py-2 text-sm font-bold" />
                <Button name="Peruuta" type="SECONDARY" action={resetProfileForm} className="mb-0 rounded-xl px-4 py-2 text-sm font-bold" />
              </>
            )}
          </div>
        </div>

        <div className="grid gap-3 rounded-2xl bg-slate-50 p-4 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-center">
          <div className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Nimi</div>
          {isEditingProfile ? (
            <TextField size="small" value={profileForm.name} onChange={handleProfileChange('name')} placeholder="Syötä nimi" fullWidth />
          ) : (
            <div className="break-words text-base font-semibold text-slate-800">{profileName || '-'}</div>
          )}

          <div className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Puhelin</div>
          {isEditingProfile ? (
            <TextField size="small" value={profileForm.phone} onChange={handleProfileChange('phone')} placeholder="Syötä puhelinnumero" fullWidth />
          ) : (
            <div className="break-words text-base font-semibold text-slate-800">{profilePhone || '-'}</div>
          )}

          <div className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Sähköposti</div>
          {isEditingProfile ? (
            <TextField size="small" type="email" value={profileForm.email} onChange={handleProfileChange('email')} placeholder="Syötä sähköposti" fullWidth />
          ) : (
            <div className="break-all text-base font-semibold text-slate-800">{profileEmail || '-'}</div>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-2xl font-bold text-slate-900">Omat myyjätiedot</span>
          <Button name="Lisää uusi" type="PRIMARY" action={() => navigate('/lisaa_yritys')} className="mb-0 rounded-xl px-4 py-2 text-sm font-bold" />
        </div>
      </div>

      {auth?.companies ? (
        <div className="mt-4">
          {auth.companies.map((comp) => {
            return (
              <CompanyCard
                company={comp}
                id={comp.companyID}
                key={comp.companyID}
                panel={comp.companyID}
                selectView={selectView}
                selectedCompanyID={selectedCompanyID}
              />
            );
          })}
        </div>
      ) : (
        <div className="center-text">
          <p>Loading</p>
        </div>
      )}

      {isPasswordModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-slate-900">Vaihda salasana</h2>
              <p className="mt-1 text-sm text-slate-600">Anna uusi salasana kahdesti. Minimipituus on 8 merkkiä.</p>
            </div>

            <div className="flex flex-col gap-3">
              <TextField
                size="small"
                type="password"
                label="Uusi salasana"
                value={passwordForm.password}
                onChange={handlePasswordChange('password')}
                autoComplete="new-password"
                fullWidth
              />
              <TextField
                size="small"
                type="password"
                label="Salasana uudelleen"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange('confirmPassword')}
                autoComplete="new-password"
                fullWidth
              />
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button name="Peruuta" type="SECONDARY" action={closePasswordModal} className="mb-0 rounded-xl px-4 py-2 text-sm font-bold" />
              <Button name="Tallenna" type="PRIMARY" action={savePassword} className="mb-0 rounded-xl px-4 py-2 text-sm font-bold" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default MyProfile;